import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Grid, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useNavigation } from "../NavigationProvider";
import { useCallback, useEffect, useState } from "react";
import { Explore as ExploreIcon } from "@mui/icons-material";
import moment from "moment/moment";
import mapboxgl from "mapbox-gl";
import axios from "axios";
import { MapboxPickerToLocation } from "../../../../common/maphelpers";
import { formatText } from "../../../../common/helpers";
import { FormatDistance } from "../navcore/common/helpers";


const useStyles = makeStyles()(
  (theme) => ({
    root: {
      zIndex: 10,
      width: "100%",
    },
    background: {
      minHeight: "32px",
      width: "100%",
      background: "#FFF",
      boxShadow: "3px 10px 3px 0px rgba(0, 0, 0, 0.00), 2px 6px 3px 0px rgba(0, 0, 0, 0.01), 1px 4px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px 0px rgba(0, 0, 0, 0.09), 0px 0px 1px 0px rgba(0, 0, 0, 0.10)",
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
    },
    grid: {
      width: "100%",
      height: "100%",
      position: "relative"
    },
    divider: {
      width: "100%"
    },
    betaTag: {
      position: "absolute",
      left: 0,
      bottom: "calc(100% + 8px)",
      background: "#FFF",
      "@media (min-width: 416px)": {
        left: "unset",
        right: 0,
      }
    },
    recenterBox: {
      position: "absolute",
      right: 0,
      bottom: "100%",
      background: "#FFF",
      borderRadius: "50%",
      boxShadow: "3px 10px 3px 0px rgba(0, 0, 0, 0.00), 2px 6px 3px 0px rgba(0, 0, 0, 0.01), 1px 4px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 2px 0px rgba(0, 0, 0, 0.09), 0px 0px 1px 0px rgba(0, 0, 0, 0.10)",
      transform: `translateY(100%)`,
      willChange: "transform",
      transition: "transform 0.25s !important",
      zIndex: -2,
      "@media (min-width: 416px)": {
        right: "unset",
        left: 0,
      }
    },
    recenterBoxIn: {
      transform: `translateY(-${theme.spacing(2)})`,
    },
    suggestionButton: {
      height: "48px"
    },
    cancelButton: {
      height: "56px",
      minHeight: "100%"
    },
    largeFont: {
      lineHeight: "115%",
      fontSize: "24px",
      fontWeight: "700"
    },
    smallFont: {
      fontSize: "20px",
      fontWeight: "400"
    }
  })
);

export default function NavigationWidget(props) {
  const { className, onCancel } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { navCore, centered, suggestedDestination, setSuggestedDestination, setDestination, hasDestination } = useNavigation();

  const [timeToDest, setTimeToDest] = useState(t("navigation:common:now"));
  const [timeAtDest, setTimeAtDest] = useState("");

  const isSuggestion = !!suggestedDestination;
  const [suggestedLocation, setSuggestedLocation] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [distanceToDest, setDistanceToDest] = useState("");

  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isSuggestion) {

      setSuggestedLocation(null);
      setSuggestionLoading(true);

      axios(`https://api.mapbox.com/geocoding/v5/mapbox.places/${suggestedDestination[0]},${suggestedDestination[1]}.json?access_token=${mapboxgl.accessToken}`)
        .then(res => {

          const { features } = res.data;

          if (features.length > 0) {
            const feature = features[0];
            setSuggestedLocation(MapboxPickerToLocation(suggestedDestination, feature));
          } else {
            setSuggestedLocation(new Location({
              coordinates: suggestedDestination,
            }));
          }
        })
        .catch(err => {
          console.log("err", err);
        })
        .finally(() => {
          setSuggestionLoading(false);
        })
    }
  }, [suggestedDestination, isSuggestion]);

  useEffect(() => {
    if (!!navCore) {

      let init = false;

      const metersFn = (m, nextInstruction, timeToDestination, metersToDestination) => {
        if (isNaN(timeToDestination)) return;

        if (timeToDestination < 5) {
          setTimeToDest(t("navigation:common:now"));
          setDistanceToDest("0 m");
        } else {
          let m = moment().add(timeToDestination, "seconds");

          setTimeAtDest(m.format("HH:mm"));

          //console.log(timeToDestination);

          let duration = moment.duration(timeToDestination, "seconds");
          let hours = Math.floor(duration.asHours());
          let minutes = duration.minutes();

          if (minutes === 0 && hours === 0) {
            let seconds = duration.seconds();
            setTimeToDest(seconds < 10 ? t("navigation:common:now") : `${seconds} ${t("common:secondUnit")}`);
          } else {
            setTimeToDest(hours > 0 ? `${hours}${t("common:hourUnit")} ${minutes > 0 ? " " + minutes + t("common:minuteUnit") : ""}` : `${minutes}m`);
          }

          if (metersToDestination < 1000) {
            setDistanceToDest(`${metersToDestination} m`);
          } else {
            setDistanceToDest(`${FormatDistance(metersToDestination / 1000)} km`);
          }
        }

        if (!init) {
          init = true;
          setReady(true);
        }
      }

      navCore.instructions.on("metersToInstruction", metersFn);


      return () => {
        navCore.instructions.off("metersToInstruction", metersFn);
      }
    }
  }, [navCore, t]);


  const onCancelButton = useCallback(() => {
    if (typeof onCancel === "function") {
      onCancel();
    } else {
      navCore?.cancel();
    }
  }, [navCore, onCancel]);


  const onNavigateToSuggestion = useCallback(() => {
    setDestination(suggestedLocation);
    setSuggestedDestination(null);
  }, [suggestedLocation]);

  return (

    <Box className={`${classes.root} ${className || ""}`}>
      <Box className={classes.background}>
        {
          isSuggestion ? (
            <Grid container className={classes.grid} direction="row" alignItems="center" justifyContent="center">
              <Grid item xs>
                <Stack>
                  <Typography variant="subtitle1">
                    {suggestionLoading ? <Skeleton variant="text" width="120px" /> : (!!suggestedLocation?.address ? suggestedLocation.address : suggestedLocation?.coordinates?.join(", "))}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {suggestionLoading ? <Skeleton variant="text" width="100px" /> : (formatText(" ", suggestedLocation?.postCode, suggestedLocation?.place) || "")}
                  </Typography>
                </Stack>
              </Grid>

              <Grid item>
                <Button variant="contained" size="large" className={classes.suggestionButton} onClick={onNavigateToSuggestion} disabled={!suggestedDestination} loading={suggestionLoading}>{t("navigation:common:start")}</Button>
              </Grid>

            </Grid>
          ) : hasDestination ? (
            <Grid container className={classes.grid} direction="row" alignItems="center" justifyContent="center">
              <Grid item xs>
                <Stack>
                  <Typography variant="subtitle1" className={classes.largeFont}>
                    {!ready ? <Skeleton variant="text" width="120px" /> : timeToDest}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" className={classes.smallFont}>
                    {!ready ? <Skeleton variant="text" width="100px" /> : `${distanceToDest} • ${timeAtDest}`}
                  </Typography>
                </Stack>
              </Grid>

              <Grid item>
                <Button variant="contained" size="large" className={classes.cancelButton} onClick={onCancelButton} >{t("navigation:common:cancel")}</Button>
              </Grid>

            </Grid>
          ) : null
        }
      </Box>
      <RecenterButton className={!centered ? classes.recenterBoxIn : ""} />
      <Chip label="BETA" color="warning" size="small" variant="outlined" className={classes.betaTag} />
    </Box>
  )
}



export function RecenterButton(props) {
  const { classes } = useStyles();

  const { navCore } = useNavigation();
  const onReCenter = useCallback(() => navCore?.renderer?.recenter(), [navCore]);

  return (
    <Box className={`${classes.recenterBox} ${props.className || ""}`}>
      <IconButton size="large" color="primary" onClick={onReCenter}>
        <ExploreIcon fontSize="large" />
      </IconButton>
    </Box>
  )
} 
